import { Box } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { ComplianceExtraFieldsConfigFragment } from 'generated/resources/graphql'

import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import Select from 'modules/core/components/Select'
import i18n from 'modules/core/lib/i18n'

import { FieldSection } from './_FieldSection'
import { DefaultComplianceSettingsFormValues } from './types'

interface DefaultComplianceSettingsFormProps {
  isUpdate?: boolean
  form: FormikProps<DefaultComplianceSettingsFormValues>
  systemComplianceExtraFields: ComplianceExtraFieldsConfigFragment[]
}

export const DefaultComplianceSettingsForm = ({
  form,
  systemComplianceExtraFields,
  isUpdate = false,
}: DefaultComplianceSettingsFormProps) => {
  const complianceExtraFieldConfigOptions = systemComplianceExtraFields.map((field) => ({
    label: field.organization,
    value: field.id,
  }))

  const selectedComplianceExtraFieldConfig = systemComplianceExtraFields.find(
    (field) => field.id === form.values.complianceExtraFieldConfigId
  )

  const documentFields = selectedComplianceExtraFieldConfig?.fields
    .filter((field) => field.fieldContext === 'document')
    .sort((a, b) => a.order - b.order)

  return (
    <Box
      gap="s16"
      overflowY="auto"
      paddingRight="s6"
      style={{ maxHeight: '50vh', maxWidth: '700px' }}
    >
      <Box flexDirection="row" gap="s24" width="full">
        <LegalEntitySelector
          label={i18n.t('common.legal_entity')}
          emptyLabel={i18n.t(
            'finance.compliance_extra_field_default_settings.modal.legal_entity_placeholder'
          )}
          value={form.values.legalEntity || null}
          onChange={(newValue) => {
            if (!isUpdate) {
              form.setFieldValue('legalEntity', newValue)
            }
          }}
          error={form.errors.legalEntity ?? undefined}
          multiple={false}
          width="full"
          disabled={isUpdate}
          hideSearchIcon
        />

        <Select
          label={i18n.t('finance.compliance_extra_field_default_settings.modal.tax_agency_label')}
          value={form.values.complianceExtraFieldConfigId ?? undefined}
          onChange={(value) => {
            if (value) {
              form.setValues({
                ...form.values,
                complianceExtraFieldConfigId: value,
              })
            }
          }}
          options={complianceExtraFieldConfigOptions}
          error={form.errors.complianceExtraFieldConfigId}
          readonly={
            isUpdate ||
            (!!form.values.complianceExtraFieldConfigId &&
              complianceExtraFieldConfigOptions.length === 1)
          }
          none={i18n.t('common.none')}
          mandatory
        />
      </Box>

      <Box flexDirection="row" gap="s24" width="full" paddingTop="s16">
        <FieldSection
          title={i18n.t(
            'finance.compliance_extra_field_default_settings.modal.invoice_section_title'
          )}
          fields={documentFields ?? []}
          form={form}
          formFieldName="invoiceDefaultValues"
          getDefaultValue={(field) => field.defaultValueForStandardInvoice}
          getVisible={(field) => field.visibleForStandardInvoice}
        />

        <FieldSection
          title={i18n.t(
            'finance.compliance_extra_field_default_settings.modal.credit_note_section_title'
          )}
          fields={documentFields ?? []}
          form={form}
          formFieldName="creditNoteDefaultValues"
          getDefaultValue={(field) => field.defaultValueForCreditNote}
          getVisible={(field) => field.visibleForCreditNote}
        />
      </Box>
    </Box>
  )
}
